import React, { useMemo } from 'react';
import type { PlanPreviewFilter, ReadyFilter } from '../../../api/types';

/**
 * Filter bar skeleton — compact chips with stagger animation.
 *
 * Per chip (staggered 150ms):
 *   1. Appear: opacity 0→1, translateY(4→0) — 100ms
 *   2. Highlight: darker background, scale 0.98→1 — 100ms
 *   3. Settle: return to normal skeleton color
 *
 * After appearing, each chip gets its own shimmer with different
 * delays and durations so they don't animate in sync.
 *
 * Default: 8 chips with varied widths (narrow/medium/wide).
 * Height: 26px (compact). Gap: 8px. Aligned left.
 */

/** Size class for varied chip widths. */
type PillSize = '' | 'filter-skeleton-pill--wide' | 'filter-skeleton-pill--medium' | 'filter-skeleton-pill--narrow';

interface DefaultPill {
  size: PillSize;
  delay: string;
  shimmerDelay: string;
  shimmerDuration: string;
}

/** 8 default chips with varied widths before the plan preview arrives. */
const DEFAULT_PILLS: DefaultPill[] = [
  { size: 'filter-skeleton-pill--wide',   delay: '0s',    shimmerDelay: '0.5s', shimmerDuration: '2.0s' },
  { size: '',                              delay: '0.15s', shimmerDelay: '0.8s', shimmerDuration: '2.3s' },
  { size: 'filter-skeleton-pill--medium',  delay: '0.30s', shimmerDelay: '1.1s', shimmerDuration: '1.8s' },
  { size: 'filter-skeleton-pill--narrow',  delay: '0.45s', shimmerDelay: '1.4s', shimmerDuration: '2.5s' },
  { size: '',                              delay: '0.60s', shimmerDelay: '1.7s', shimmerDuration: '2.1s' },
  { size: 'filter-skeleton-pill--medium',  delay: '0.75s', shimmerDelay: '2.0s', shimmerDuration: '1.9s' },
  { size: 'filter-skeleton-pill--narrow',  delay: '0.90s', shimmerDelay: '2.3s', shimmerDuration: '2.4s' },
  { size: 'filter-skeleton-pill--wide',    delay: '1.05s', shimmerDelay: '2.6s', shimmerDuration: '2.0s' },
];

/** Stagger animation delays for plan-based pills. */
function pillTiming(index: number) {
  return {
    delay: `${index * 0.15}s`,
    shimmerDelay: `${0.5 + index * 0.4}s`,
    shimmerDuration: `${1.8 + (index % 3) * 0.3}s`,
  };
}

interface FilterSkeletonProps {
  /** Real filter descriptors from the plan preview (Step 1). */
  filters?: PlanPreviewFilter[] | null;
  /** Filters that have been validated and are ready to render. */
  readyFilters?: ReadyFilter[];
}

const FilterSkeleton: React.FC<FilterSkeletonProps> = ({ filters, readyFilters }) => {
  // Build a set of ready filter indices for O(1) lookup
  const readyIndices = useMemo(
    () => new Set((readyFilters ?? []).map((rf) => rf.index)),
    [readyFilters],
  );

  // Use real filter names when available, otherwise default chips
  if (filters && filters.length > 0) {
    return (
      <div className="filter-skeleton-bar">
        {filters.map((f, i) => {
          const timing = pillTiming(i);
          const isDateRange = f.type === 'date_range';
          const isFilterReady = readyIndices.has(i);
          return (
            <div
              key={f.name || i}
              className={[
                'filter-skeleton-pill',
                isDateRange ? 'filter-skeleton-pill--wide' : '',
                'filter-skeleton-pill--named',
                isFilterReady ? 'filter-skeleton-pill--ready' : '',
              ].filter(Boolean).join(' ')}
              style={
                {
                  '--pill-delay': timing.delay,
                  '--pill-shimmer-delay': timing.shimmerDelay,
                  '--pill-shimmer-duration': timing.shimmerDuration,
                } as React.CSSProperties
              }
            >
              <span className="filter-skeleton-pill__label">{f.name}</span>
              {isFilterReady && (
                <svg className="filter-skeleton-pill__check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M4 7l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="filter-skeleton-bar">
      {DEFAULT_PILLS.map((pill, i) => (
        <div
          key={i}
          className={`filter-skeleton-pill${pill.size ? ` ${pill.size}` : ''}`}
          style={
            {
              '--pill-delay': pill.delay,
              '--pill-shimmer-delay': pill.shimmerDelay,
              '--pill-shimmer-duration': pill.shimmerDuration,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
};

export default React.memo(FilterSkeleton);